import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { storage } from '@uhg-abyss/web/tools/storage';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Link } from '@uhg-abyss/web/ui/Link';
import { debounce } from 'lodash';
import mapboxgl from 'mapbox-gl';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { ChipFilters } from '../../common/ChipFiltersGroup';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { ResultFilter } from '../../common/ContentFilters/ResultFilter';
import { ResultSort } from '../../common/ContentFilters/ResultSort';
import { PortalContext } from '../../context/PortalContext';
import { ErrorBoundary } from '../../errors/ErrorBoundary';
import { useConfig } from '../../frontends/ProviderSearch/context/Analytics';
import { getEnableMapPinRedesign } from '../../frontends/ProviderSearch/routes/MapView/utils';
import { useCoverageType } from '../../hooks/useCoverageType';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useUserLocation } from '../../hooks/useUserLocation';
import { FacilityLocationsResponse } from '../../models/FacilityDetails';
import { Provider } from '../../models/Provider';
import { ProviderLocation } from '../../models/ProviderDetails';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { Directions } from '../../models/RouteDirections';
import { useMapStore } from '../../store/useMapStore';
import { MapState } from '../../store/useMapStore/mapStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import {
  applyAtlasStyleWorkarounds,
  installAtlasGlyphGzipWorkaround,
  installAtlasLabelFallbackOnIdle,
} from '../../utils/atlasMapWorkaround';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import {
  getDistanceString,
  getDurationString,
  getNavTypeString,
  getStepDistanceString,
  getStepIcon,
} from '../../utils/generalMap.utils';
import {
  Feature,
  clearRoute,
  getFeaturesArray,
  getRoute,
  getUniqueCoordinates,
  loadClusteredLocationPins,
  loadMap,
  loadResultLocationPins,
  loadRoute,
  loadUserLocationPin,
  removeAllMarkers,
  setMapCoords,
  setZoomLevel,
} from '../../utils/map.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import {
  CLUSTER_ZOOM_THRESHOLD_DEFAULT,
  Constants,
  ReverseCoverageTypesCodes,
} from '../Constants';
import { mobileOnly } from '../ConstantsStyles';
import { PortalIdentifier } from '../Portal';
import { ClusterCarousel } from './ClusterCarousel';
import { MapControlDesktop } from './MapControlDesktop';
import { MapControlMobile } from './MapControlMobile';
import { MapDirections } from './MapDirections';
import {
  MapContainer,
  MapDirectionsContainer,
  NavigationContainer,
  NavigationContainer2,
} from './MapDisplay.styled';

type Props = {
  closeSidePanel?: boolean;
  navigationRef?: React.MutableRefObject<HTMLElement | null>;
  coords: { latitude: string; longitude: string };
  disabledPinAction?: boolean;
  fullWidth?: boolean;
  headers?: ResponseHeaders;
  locationResults?: ProviderLocation[] | FacilityLocationsResponse[];
  map: any;
  isOpenCompareShare?: boolean;
  providerPins?: Provider[];
  providerResults?: Provider[];
  routeEndCoords?: [number | null, number | null];
  setRouteEndCoords?(coords: [number | null, number | null]): void;
  selectedItems?: any[];
  mobileMapControlChildren?: React.ReactNode;
  mobileRouteView?: boolean;
  setPopupContent?(
    content: ProviderLocation | FacilityLocationsResponse | null
  ): void;
  refToBeFocusedOnDirectionsClose?: HTMLElement;
  updatePin?(
    content: ProviderLocation | FacilityLocationsResponse | null
  ): void;
  selectLocation?: (locationId, locationLng, locationLat) => void;
  directions?: Directions;
  mobileScreen?: boolean;
  isLocationsTab?: boolean;
  previousLocation?: string;
  setPreviousLocation?: (a: string) => void;
  enableMapFilterSortCTA?: boolean;
  expandedProviderListView?: boolean;
  setExpandedProviderListView?: (a: boolean) => void;
  setNavigateToDirections?: (a: boolean) => void;
  setGroupedLocationDrawerOpen?: (a: boolean) => void;
  setDrawerContent?: (a: any) => void;
  skipNavigationFocus?: boolean;
  shouldResetSelectedId?: boolean;
  onClick?: () => void;
};

export const MapDisplay = ({
  coords,
  disabledPinAction = false,
  fullWidth = false,
  isOpenCompareShare = false,
  providerPins = [],
  providerResults = [],
  locationResults = [],
  map,
  closeSidePanel,
  routeEndCoords = [null, null],
  setRouteEndCoords,
  mobileMapControlChildren,
  mobileRouteView,
  setPopupContent,
  refToBeFocusedOnDirectionsClose,
  updatePin,
  selectLocation,
  directions,
  headers,
  mobileScreen = false,
  isLocationsTab = false,
  previousLocation,
  setPreviousLocation,
  enableMapFilterSortCTA,
  expandedProviderListView,
  setExpandedProviderListView,
  setNavigateToDirections,
  setGroupedLocationDrawerOpen,
  setDrawerContent,
  skipNavigationFocus = false,
  shouldResetSelectedId = true,
  navigationRef,
}: Props) => {
  const { t } = useTranslation();
  const mapContainerTestId: string = 'map-view-container';
  const { longitude, latitude } = coords;
  const enableScreenZoom = useMediaQuery(mobileOnly);

  const mapOnPremKey = useConfig('MAPBOX_ONPREM_KEY');
  const mapOnPremURL = useConfig('MAPBOX_ONPREM_URL');
  const clusterMaxZoom = useConfig('MAP_CLUSTER_MAX_ZOOM');
  const clusterRadius = useConfig('MAP_CLUSTER_RADIUS');
  const clusterRadiusZoomBreakpoint = useConfig(
    'MAP_CLUSTER_RADIUS_ZOOM_BREAKPOINT'
  );
  const clusterRadiusLowZoom = useConfig('MAP_CLUSTER_RADIUS_LOW_ZOOM');
  const clusterRadiusHighZoom = useConfig('MAP_CLUSTER_RADIUS_HIGH_ZOOM');
  const groupDistanceThreshold = useConfig(
    'MAP_GROUP_DISTANCE_THRESHOLD_MILES'
  );
  const { portalData } = useContext(PortalContext);

  const [navType, setNavType] = useState<string>('driving-traffic');
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);
  const [currentRoute, setCurrentRoute] = useState<number>(0);
  const [showStepInstructions, setShowStepInstructions] =
    useState<boolean>(false);
  const [sortFilterOpen, setSortFilterOpen] = useState(
    storage.session.get('sortFilterOpen')
  );
  const [allFiltersOpen, setAllFiltersOpen] = useState(
    storage.session.get('allFiltersOpen')
  );

  const [mapDirections, setMapDirections] = useState<Directions>(
    directions || {
      userPlaceName: '',
      endPlaceName: '',
      routes: [],
    }
  );

  const [, getUserLocation] = useUserLocation({});

  const mapContainerRef = React.useRef<any>(null);
  const navigationContainerRef = useRef<HTMLElement>();

  const [highlightId, setHighlightId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_HIGHLIGHT_ID,
    { providerId: '', from: '' }
  );

  const [, setSelectedId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    null
  );

  const [, setMapPinCoords] = useSessionStorage<
    (string[] | (string | undefined)[])[]
  >(Constants.STORAGE_KEYS.SESSION.MAP_PIN_COORDS, []);

  const [
    enableMapEnhancements,
    enableMapViewLargeResults,
    enableMapPinRedesignFlag,
    enableMapDragRefreshResults,
    enableMapViewHideOption,
    enableSyncMapPinsWithList,
    enableClusterDebugOverlay,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_LARGE_RESULTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_PIN_REDESIGN,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_DRAG_REFRESH_RESULTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SYNC_MAP_PINS_WITH_LIST,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_CLUSTER_DEBUG_OVERLAY,
  ]);

  const forceMapPinRedesign = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.FORCE_MAP_PIN_REDESIGN
  );
  const enableMapPinRedesign = getEnableMapPinRedesign(
    enableMapPinRedesignFlag,
    forceMapPinRedesign
  );

  const {
    mapLatitude,
    mapLongitude,
    mapSearchRadius,
    setMapLatitude,
    setMapLongitude,
    setMapSearchRadius,
  } = useMapStore(
    useShallow((state: MapState) => ({
      mapLatitude: state.mapLatitude,
      mapLongitude: state.mapLongitude,
      mapSearchRadius: state.mapSearchRadius,
      setMapLatitude: state.setMapLatitude,
      setMapLongitude: state.setMapLongitude,
      setMapSearchRadius: state.setMapSearchRadius,
    }))
  );

  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;
  const additionalCss = isGEHAV2
    ? '$core.color.brand.100'
    : '$descriptionBlack';

  const { search, sectionType } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
      sectionType: state.typeaheadSearchStore.sectionType,
    }))
  );

  const doSetMapCoords = (isZoomChanged: boolean) => {
    setMapCoords(
      map.current,
      mapLatitude,
      mapLongitude,
      mapSearchRadius,
      setMapLatitude,
      setMapLongitude,
      setMapSearchRadius,
      isZoomChanged
    );
  };

  const handleMapPins = async (
    featuresAr: Feature[],
    longitude: string,
    latitude: string,
    mobileScreen: boolean,
    map: React.MutableRefObject<mapboxgl.Map | null>,
    setHighlightId: (highlightData: {
      providerId: string;
      from: string;
    }) => void,
    disabledPinAction: boolean,
    updatePin:
      | ((content: ProviderLocation | FacilityLocationsResponse | null) => void)
      | undefined,
    enableMapEnhancements: boolean,
    enableMapViewLargeResults: boolean,
    setMapPinCoords: (coordinates: string[][]) => void,
    removeAllMarkers: () => void,
    setZoomLevel: (
      map: mapboxgl.Map | null,
      coordinates: (string[] | (string | undefined)[])[]
    ) => Promise<void>,
    loadResultLocationPins: (
      mobileScreen: boolean,
      map: mapboxgl.Map | null,
      features: any[],
      coordinates: (string[] | (string | undefined)[])[],
      setHighlightId: (highlightData: {
        providerId: string;
        from: string;
      }) => void,
      disabledPinAction: boolean,
      updatePin:
        | ((
            content: ProviderLocation | FacilityLocationsResponse | null
          ) => void)
        | undefined,
      enableMapEnhancements: boolean,
      enableMapViewLargeResults: boolean
    ) => Promise<void>,
    enableMapPinRedesign: boolean
  ): Promise<void> => {
    if (!featuresAr?.length) {
      return;
    }
    const uniqueCoordinates = getUniqueCoordinates(
      featuresAr,
      longitude,
      latitude
    );
    await setZoomLevel(map.current, uniqueCoordinates);
    removeAllMarkers();
    if (enableMapPinRedesign) {
      await loadClusteredLocationPins(
        map.current,
        featuresAr,
        setHighlightId,
        updatePin,
        enableMapEnhancements,
        mobileScreen,
        disabledPinAction,
        enableMapViewLargeResults,
        clusterMaxZoom,
        clusterRadius,
        groupDistanceThreshold,
        setGroupedLocationDrawerOpen,
        setDrawerContent,
        clusterRadiusZoomBreakpoint,
        clusterRadiusLowZoom,
        clusterRadiusHighZoom,
        enableSyncMapPinsWithList,
        enableClusterDebugOverlay
      );
      // Zoom nudge from DE1128287 to force clustered pins to render on load.
      // Side effect: it cancels the fitBounds animation so the map can't frame
      // all pins, which is why it's gated off for the sync-pins-with-list flow.
      // TODO: remove once verified DE1128287 (pins not showing) no longer
      // reproduces without this nudge (deferred updateMarkers in
      // loadClusteredLocationPins may already cover it).
      if (!enableSyncMapPinsWithList) {
        const currentMapZoomLevel =
          map?.current?.getZoom() || CLUSTER_ZOOM_THRESHOLD_DEFAULT;
        map?.current?.setZoom(currentMapZoomLevel + 0.001);
      }
    } else {
      setMapPinCoords(uniqueCoordinates);
      await loadResultLocationPins(
        mobileScreen,
        map.current,
        featuresAr,
        uniqueCoordinates,
        setHighlightId,
        disabledPinAction,
        updatePin,
        enableMapEnhancements,
        enableMapViewLargeResults
      );
    }
  };

  const newMap = async () => {
    const atlasHostUrl = (mapOnPremURL || 'https://map.int.ce.uhg.com').replace(
      /\/+$/,
      ''
    );
    // Use same-origin proxy path to avoid CORS — Next.js rewrites /atlas-map-proxy/* to Atlas origin
    // const atlasAssetBaseUrl = '/atlas-map-proxy';

    mapboxgl.baseApiUrl = atlasHostUrl;
    installAtlasGlyphGzipWorkaround();
    const styleResponse = await fetch(
      `${atlasHostUrl}/data/styles/mapbox/streets-v12.json`
    );
    const style = await styleResponse.json();

    const pmtilesUrl = `${atlasHostUrl}/tilesets/mapbox.mapbox-streets-v8-20260602.pmtiles`;

    applyAtlasStyleWorkarounds(style, atlasHostUrl, pmtilesUrl);

    map.current = new mapboxgl.Map({
      accessToken: mapOnPremKey,
      container: mapContainerRef.current,
      style,
      center: [Number(longitude), Number(latitude)],
      zoom: Constants.DEVICE_LOCATION.ZOOM,
    });
    clearRoute(map.current, setRouteEndCoords);
    setHighlightId({ providerId: '', from: '' });
    map.current.on('load', async () => {
      await loadMap(
        map.current,
        setHighlightId,
        setPopupContent,
        enableMapEnhancements
      );

      loadUserLocationPin(map.current, longitude, latitude, isGehaTheme);

      const featuresAr = getFeaturesArray(
        enableMapPinRedesign,
        providerPins,
        providerResults,
        locationResults
      );

      await handleMapPins(
        featuresAr,
        longitude,
        latitude,
        mobileScreen,
        map,
        setHighlightId,
        disabledPinAction,
        updatePin,
        enableMapEnhancements,
        enableMapViewLargeResults,
        setMapPinCoords,
        removeAllMarkers,
        setZoomLevel,
        loadResultLocationPins,
        enableMapPinRedesign
      );
      setIsMapLoaded(true);

      if (map.current) {
        installAtlasLabelFallbackOnIdle(map.current);
      }
    });

    // TODO 5/20 - This likely will no longer be needed depending on final map redesign decisions. Also likely will need additional fixes if it's actually enabled
    map.current.on('moveend', (e) => {
      if (
        enableMapPinRedesign &&
        enableMapDragRefreshResults &&
        !isOpenCompareShare &&
        'originalEvent' in e
      ) {
        const debounceSetMapCoords = debounce(doSetMapCoords, 500);
        debounceSetMapCoords(e.originalEvent.type === 'wheel');
      }
    });
  };

  useEffect(() => {
    setMapLatitude(undefined);
    setMapLongitude(undefined);
    setMapSearchRadius(undefined);
    removeAllMarkers();
    setMapPinCoords([]);

    return () => {
      setMapLatitude(undefined);
      setMapLongitude(undefined);
      setMapSearchRadius(undefined);
      removeAllMarkers();
      setMapPinCoords([]);
    };
  }, []);

  useEffect(() => {
    if (sortFilterOpen || allFiltersOpen) {
      handleCloseNav();
    }

    const intervalId = setInterval(() => {
      const currentSortFilterOpen = storage.session.get('sortFilterOpen');
      const currentAllFiltersOpen = storage.session.get('allFiltersOpen');
      if (currentSortFilterOpen !== sortFilterOpen) {
        setSortFilterOpen(currentSortFilterOpen);
      }
      if (currentAllFiltersOpen !== allFiltersOpen) {
        setAllFiltersOpen(currentAllFiltersOpen);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [sortFilterOpen, allFiltersOpen]);

  useEffect(() => {
    if (directions) {
      setMapDirections(directions);
      setShowStepInstructions(false);
      setCurrentRoute(0);
    }
  }, [directions]);

  useEffect(() => {
    setMapLatitude(undefined);
    setMapLongitude(undefined);
    setMapSearchRadius(undefined);
    removeAllMarkers();
    setMapPinCoords([]);
    if (isMapLoaded) {
      loadUserLocationPin(map.current, longitude, latitude, isGehaTheme);
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (!isMapLoaded && mapboxgl?.config != undefined) {
      const initMap = async () => {
        try {
          await newMap();
        } catch (e) {
          throw e;
        }
      };
      void initMap();
    } else {
      const featuresAr = getFeaturesArray(
        enableMapPinRedesign,
        providerPins,
        providerResults,
        locationResults
      );

      handleMapPins(
        featuresAr,
        longitude,
        latitude,
        mobileScreen,
        map,
        setHighlightId,
        disabledPinAction,
        updatePin,
        enableMapEnhancements,
        enableMapViewLargeResults,
        setMapPinCoords,
        removeAllMarkers,
        setZoomLevel,
        loadResultLocationPins,
        enableMapPinRedesign
      );
    }
  }, [
    enableMapPinRedesign
      ? JSON.stringify(providerPins)
      : JSON.stringify(providerResults),
    JSON.stringify(locationResults),
  ]);

  useEffect(() => {
    const getMapRoute = async () => {
      const routeDirections = await getRoute(
        map.current,
        longitude,
        latitude,
        routeEndCoords[0],
        routeEndCoords[1],
        getUserLocation,
        false,
        navType,
        0,
        mapOnPremKey,
        mapOnPremURL
      );
      if (routeDirections) setMapDirections(routeDirections);
    };
    if (routeEndCoords[0]) {
      setCurrentRoute(0);
      getMapRoute();
    }
  }, [navType, map.current]);

  const hasRoute = routeEndCoords[0];

  useEffect(() => {
    setNavType('driving-traffic');
    if (!isWidget && !skipNavigationFocus)
      if (hasRoute === null) {
        refToBeFocusedOnDirectionsClose?.focus();
      } else {
        navigationContainerRef?.current?.focus();
      }
  }, [hasRoute]);

  useEffect(() => {
    if (map.current) map.current.resize();
  }, [closeSidePanel, mobileRouteView]);
  const directionButtonRef = useRef<HTMLElement | null>(null);

  const handleCloseNav = () => {
    clearRoute(map.current, setRouteEndCoords, highlightId.providerId);
    directionButtonRef.current?.focus();
    if (shouldResetSelectedId) setSelectedId(null);
  };
  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      if (
        event.target instanceof HTMLElement &&
        event.target.id?.includes('miles-button') &&
        directionButtonRef
      ) {
        directionButtonRef.current = event.target;
      }
    };

    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, []);
  const handleBackList = () => {
    adobeLinkTrackEvent({
      name: 'back',
      location: `body:view on list`,
      type: 'internal',
    });

    setNavigateToDirections(false);
    setExpandedProviderListView(true);
  };

  const mapControl = () => {
    const screen = enableMapEnhancements ? enableScreenZoom : mobileScreen;
    if (mobileScreen) {
      return (
        <MapControlMobile
          controlChildren={mobileMapControlChildren}
          enableMapEnhancements={enableMapEnhancements}
          isLocationsTab={isLocationsTab}
          map={map.current}
          mobileRouteView={!!routeEndCoords[0]}
        />
      );
    }

    return (
      <MapControlDesktop
        map={map.current}
        showZoomButtons={enableMapEnhancements && screen}
      />
    );
  };

  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const urlParams = new URLSearchParams(window.location.search);
  const choosePCPID = urlParams.get('choosePCPId') || '';

  const coverageTypeData = useCoverageType();
  const resultFilterName =
    coverageTypeData === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']
      ? t('FILTER_BUTTONS.All_FILTERS')
      : t('CONTENT_FILTERS.ALL_FILTERS');

  const handleNavigationKeydown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCloseNav();
    }
  };

  const renderOldMap = () => (
    <NavigationContainer
      cssProps={{
        hasRoute,
        enableMapEnhancements: !enableMapFilterSortCTA,
      }}
      id="navigation-container-id"
      ref={navigationContainerRef}
      tabIndex={-1}
    >
      {hasRoute && mapDirections?.routes?.length && !isWidget ? (
        <MapDirections
          currentRoute={currentRoute}
          getDistanceString={getDistanceString}
          getDurationString={getDurationString}
          getNavTypeString={getNavTypeString}
          getStepDistanceString={getStepDistanceString}
          getStepIcon={getStepIcon}
          handleCloseNav={handleCloseNav}
          latitude={latitude}
          loadRoute={loadRoute}
          longitude={longitude}
          map={map}
          mapContainerRef={mapContainerRef}
          mapContainerTestId={mapContainerTestId}
          mapDirections={mapDirections}
          mobileScreen={mobileScreen}
          navType={navType}
          navigationContainerRef={navigationRef ?? { current: null }}
          routeEndCoords={routeEndCoords}
          setCurrentRoute={setCurrentRoute}
          setNavType={setNavType}
          setShowStepInstructions={setShowStepInstructions}
          showStepInstructions={showStepInstructions}
        />
      ) : (
        <>
          {enableMapFilterSortCTA ? (
            <Flex alignItems="flex-end" justify="start" style={{ gap: '16px' }}>
              <ResultFilter
                color={additionalCss}
                icon="filter_list"
                name={resultFilterName}
                size="$md"
              />
              <ResultSort isMapView sectionType={sectionType} />
            </Flex>
          ) : (
            enableMapEnhancements &&
            !mobileScreen && (
              <Flex
                alignItems="flex-end"
                justify="start"
                style={{ gap: '16px' }}
              >
                <ResultFilter
                  color={additionalCss}
                  icon="filter_list"
                  name={resultFilterName}
                  size="$md"
                />
                <ResultSort isMapView sectionType={sectionType} />
              </Flex>
            )
          )}
        </>
      )}
    </NavigationContainer>
  );

  const renderNewMap = () => {
    const showDialog =
      (hasRoute && mapDirections?.routes?.length && !isWidget) ||
      (!enableMapFilterSortCTA &&
        enableMapEnhancements &&
        !mobileScreen &&
        !isLocationsTab &&
        !window.location.href?.includes('choose-pcp') &&
        choosePCPID.length <= 0 &&
        !isWidget);
    if (!showDialog) return null;

    return (
      <NavigationContainer2
        aria-label={t('Directions')}
        aria-modal={showDialog}
        cssProps={{ isLocationsTab, mobileScreen, hasRoute }}
        id="navigation-container-id"
        onKeyDown={handleNavigationKeydown}
        ref={navigationContainerRef}
        role="dialog"
        tabIndex={-1}
      >
        {hasRoute && mapDirections?.routes?.length && !isWidget ? (
          <>
            {!enableMapFilterSortCTA &&
              !isLocationsTab &&
              !mobileScreen &&
              choosePCPID.length <= 0 &&
              !isWidget && (
                <Flex
                  alignItems="flex-end"
                  justify="start"
                  style={{ gap: '16px' }}
                >
                  <ResultFilter
                    color={additionalCss}
                    icon="filter_list"
                    name={resultFilterName}
                    size="$md"
                  />
                  <ResultSort isMapView sectionType={sectionType} />
                </Flex>
              )}
            <MapDirectionsContainer cssProps={{ isLocationsTab, mobileScreen }}>
              <MapDirections
                currentRoute={currentRoute}
                getDistanceString={getDistanceString}
                getDurationString={getDurationString}
                getNavTypeString={getNavTypeString}
                getStepDistanceString={getStepDistanceString}
                getStepIcon={getStepIcon}
                handleCloseNav={handleCloseNav}
                latitude={latitude}
                loadRoute={loadRoute}
                longitude={longitude}
                map={map}
                mapContainerRef={mapContainerRef}
                mapContainerTestId={mapContainerTestId}
                mapDirections={mapDirections}
                mobileScreen={mobileScreen}
                navType={navType}
                navigationContainerRef={navigationRef ?? { current: null }}
                routeEndCoords={routeEndCoords}
                setCurrentRoute={setCurrentRoute}
                setNavType={setNavType}
                setShowStepInstructions={setShowStepInstructions}
                showStepInstructions={showStepInstructions}
              />
            </MapDirectionsContainer>
          </>
        ) : (
          <>
            {!enableMapFilterSortCTA &&
              enableMapEnhancements &&
              !mobileScreen &&
              !isLocationsTab &&
              !window.location.href?.includes('choose-pcp') &&
              choosePCPID.length <= 0 &&
              !isWidget && (
                <Flex
                  alignItems="flex-end"
                  css={{
                    '@media (max-width: 964px)': {
                      maxWidth: '375px',
                    },
                    '@media (max-width: 771px)': {
                      maxWidth: '180px',
                    },
                  }}
                  justify="start"
                  style={{ gap: '16px' }}
                >
                  <ResultFilter
                    color={additionalCss}
                    icon="filter_list"
                    name={resultFilterName}
                    size="$md"
                  />
                  <ErrorBoundary>
                    <ChipFilters
                      enableMapFilterSortCTA={enableMapFilterSortCTA}
                      previousLocation={previousLocation}
                      searchResults={{ providers: providerResults || [] }}
                      sectionType={sectionType || ''}
                      setPreviousLocation={setPreviousLocation}
                    />
                  </ErrorBoundary>
                  <ResultSort isMapView sectionType={sectionType} />
                </Flex>
              )}
          </>
        )}
      </NavigationContainer2>
    );
  };

  return (
    <React.Fragment>
      {!mobileScreen && enableMapViewHideOption && expandedProviderListView && (
        <Flex css={{ margin: '24px 0 24px 16px' }}>
          <Link
            data-auto-testid="back-to-provider-list-view"
            data-testid="back-to-provider-list-view"
            isStandardAnchor
            onClick={() => handleBackList()}
          >
            <IconSymbol
              color="$core.color.brand.60"
              css={{ width: '20px', height: '20px', marginRight: '4px' }}
              icon="arrow_back"
              title="Back_to-results"
            />
            {t('BACK_TO_RESULTS')}
          </Link>
        </Flex>
      )}
      <MapContainer
        // @ts-expect-error This is a eslint error suggested by --fix command
        cssProps={{
          enableMapEnhancements,
          fullWidth,
          isOpenCompareShare,
          mobileRouteView,
          hasRoute,
          isWidget,
          isGehaTheme,
        }}
      >
        <div
          className="map-container"
          data-auto-testid={mapContainerTestId}
          data-testid={mapContainerTestId}
          ref={mapContainerRef}
        />
        {mapControl()}
        {enableMapEnhancements ? renderNewMap() : renderOldMap()}
      </MapContainer>
      <ClusterCarousel
        data={[]}
        headers={headers}
        search={search}
        sectionType={sectionType}
        selectLocation={selectLocation}
      />
    </React.Fragment>
  );
};
